import { useState, useEffect } from 'react';
import api from '../../../services/api';
import { getStorefrontLab, getLabCheckoutPolicies } from '../services/labApi';

/**
 * Loads the storefront lab, checkout policy bundle, and payment config.
 * Pure data concern, kept out of the page orchestrator.
 *
 * @param slug storefront slug
 * @param onPolicyError optional callback invoked with a message if policies fail
 */
export default function useLabCheckoutData(slug, { onPolicyError } = {}) {
  const [lab, setLab] = useState(null);
  const [labLoading, setLabLoading] = useState(true);
  const [labError, setLabError] = useState(null);

  const [checkoutPolicies, setCheckoutPolicies] = useState({});
  const [checkoutPoliciesMissing, setCheckoutPoliciesMissing] = useState([]);
  const [checkoutPoliciesLoading, setCheckoutPoliciesLoading] = useState(true);

  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paymentConfigLoading, setPaymentConfigLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLabLoading(true);
    getStorefrontLab(slug)
      .then(setLab)
      .catch(err => setLabError(err.message || 'Lab panel not found'))
      .finally(() => setLabLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    setCheckoutPoliciesLoading(true);
    getLabCheckoutPolicies(slug)
      .then((result) => {
        setCheckoutPolicies(result.policies || {});
        setCheckoutPoliciesMissing(result.missing || []);
      })
      .catch((err) => {
        setCheckoutPolicies({});
        setCheckoutPoliciesMissing(['hipaa', 'terms', 'telehealth']);
        if (typeof onPolicyError === 'function') {
          onPolicyError(err.message || 'Failed to load checkout policies.');
        }
      })
      .finally(() => setCheckoutPoliciesLoading(false));
  }, [slug]);

  useEffect(() => {
    setPaymentConfigLoading(true);
    api.getPaymentConfig()
      .then(setPaymentConfig)
      .catch(() => {})
      .finally(() => setPaymentConfigLoading(false));
  }, []);

  return {
    lab, labLoading, labError,
    checkoutPolicies, checkoutPoliciesMissing, checkoutPoliciesLoading,
    paymentConfig, paymentConfigLoading,
  };
}
